import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TickerWithName from "../components/TickerWithName";
import api from "../utils/api";
import {
    QuickTradeEquityButton,
    QuickTradeOptionButton,
} from "../components/QuickTradeButtons";

// helpers
const money2 = (v) => {
    if (v == null || v === "") return "0.00";
    const num = Number(v);
    if (!Number.isFinite(num)) return String(v);
    return num.toFixed(2);
};

const pctChange = (a, b) => {
    const x = Number(a);
    const y = Number(b);
    if (!Number.isFinite(x) || !Number.isFinite(y) || x === 0) return null;
    return ((y - x) / x) * 100;
};

const EstExitLine = ({ entry, exit }) => {
    const pct = pctChange(entry, exit);
    const good = pct !== null && pct >= 0;
    return (
        <div className="text-sm">
            Est Exit: ${money2(exit)}{" "}
            {pct !== null && (
                <span
                    className={`ml-1 px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap ${
                        good ? "bg-green-600" : "bg-red-600"
                    }`}
                >
                    {good ? "+" : ""}
                    {pct.toFixed(2)}%
                </span>
            )}
        </div>
    );
};

const DailyPicks = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        const fetchPicks = async () => {
            try {
                setLoading(true);
                setError("");
                const res = await api.get("/api/daily-picks");
                if (cancelled) return;
                setData(res.data || {});
            } catch (e) {
                if (!cancelled) {
                    console.error("daily-picks error", e);
                    setError("Failed to load Daily Picks.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        fetchPicks();
        return () => {
            cancelled = true;
        };
    }, []);

    const topPicks = (data?.top_picks || data?.topPicks || []) || [];
    const optionsPick = data?.optionsPick || data?.options_pick || null;

    return (
        <div className="p-4 sm:p-6 text-white">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-blue-400 drop-shadow">
                🔥 Daily Picks
            </h2>

            {loading && <div className="text-gray-400 mb-2">Loading…</div>}
            {error && <div className="text-red-400 text-sm mb-3">{error}</div>}

            {/* Top stock picks */}
            <h3 className="text-xl sm:text-2xl font-bold mb-3 text-blue-300">
                Top Stock Picks
            </h3>

            {topPicks.length === 0 ? (
                <div className="text-sm text-gray-400 mb-6">
                    No stock picks available yet today.
                </div>
            ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-6">
                    {topPicks.map((pick) => (
                        <div
                            key={pick.ticker}
                            className="bg-gray-900/70 border border-gray-700 rounded-2xl p-3 sm:p-4 hover:border-blue-500 hover:shadow-lg transition cursor-pointer"
                            onClick={() => navigate(`/chart/${pick.ticker}`)}
                        >
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <TickerWithName symbol={pick.ticker} />
                                        <span className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-blue-700/80 text-blue-100 text-[11px] font-semibold whitespace-nowrap shadow">
                                            Score{" "}
                                            {pick.score != null
                                                ? Number(pick.score).toFixed
                                                    ? Number(pick.score).toFixed(2)
                                                    : pick.score
                                                : "–"}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-400 mt-1">
                                        From Daily Picks engine
                                    </div>
                                </div>
                                <div className="text-right text-sm sm:text-base">
                                    <div>Entry: ${money2(pick.entryPrice)}</div>
                                    <div className="text-green-400">
                                        Est Exit: ${money2(pick.estimatedExit)}
                                    </div>
                                </div>
                            </div>

                            <div className="text-sm text-gray-300 mb-2">
                                <span className="font-semibold text-blue-300">
                                    Reason:{" "}
                                </span>
                                {pick.reason}
                            </div>

                            <QuickTradeEquityButton ticker={pick.ticker} />
                        </div>
                    ))}
                </div>
            )}

            {/* Featured options play */}
            <h3 className="text-xl sm:text-2xl font-bold mt-6 mb-3 sm:mt-10 sm:mb-4 text-blue-300">
                Featured Options Play
            </h3>

            {optionsPick ? (
                <div
                    className="bg-blue-900/80 border border-blue-700 rounded-2xl p-3 sm:p-4 hover:border-blue-400 hover:shadow-lg cursor-pointer space-y-1"
                    onClick={() => navigate(`/chart/${optionsPick.ticker}`)}
                >
                    <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                            <div className="flex flex-wrap items-center gap-2">
                                <TickerWithName symbol={optionsPick.ticker} />
                                {optionsPick.strategy && (
                                    <span
                                        className={`ml-1 px-2 py-0.5 text-xs font-bold rounded whitespace-nowrap ${
                                            String(optionsPick.strategy)
                                                .toUpperCase()
                                                .startsWith("C")
                                                ? "bg-green-600"
                                                : "bg-red-600"
                                        }`}
                                    >
                                        {String(optionsPick.strategy).toUpperCase()}
                                    </span>
                                )}
                            </div>
                            <div className="text-xs text-blue-200 mt-1">
                                Featured by Options engine
                            </div>
                        </div>

                        <div className="text-right text-sm sm:text-base">
                            <div>Strike: {optionsPick.strike}</div>
                            <div>Expiration: {optionsPick.expiration}</div>
                            {optionsPick.confidence != null && (
                                <div className="text-blue-100">
                                    Confidence: {optionsPick.confidence}%
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="text-sm text-gray-100">
                        <div>Entry: ${money2(optionsPick.entryPrice)}</div>
                        <EstExitLine
                            entry={optionsPick.entryPrice}
                            exit={optionsPick.estimatedExit}
                        />
                    </div>

                    {optionsPick.reason && (
                        <div className="text-sm text-gray-200 mt-1">
                            <span className="font-semibold text-blue-100">
                                Reason:{" "}
                            </span>
                            {optionsPick.reason}
                        </div>
                    )}

                    <div className="mt-3 flex flex-wrap gap-2">
                        <QuickTradeOptionButton
                            ticker={optionsPick.ticker}
                            strategy={optionsPick.strategy}
                            strike={optionsPick.strike}
                            expiration={optionsPick.expiration}
                        />
                        <QuickTradeEquityButton ticker={optionsPick.ticker} />
                    </div>
                </div>
            ) : (
                <div className="text-sm text-gray-400">
                    No featured options play today.
                </div>
            )}
        </div>
    );
};

export default DailyPicks;
